import { Input, message } from 'antd'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useRef, useState } from 'react'
import { CopyText } from '~/components/business/CopyText'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyIcon } from '~/components/ui/MyIcon'
import {
  AiCreateArtworkModel,
  getGeneratorTags_aiArtworkResourceModule_spellTab,
  getGeneratorTags_aiArtworkResourceModule_spellTab_tags,
  getTagsCategory_aiArtworkResourceModule_tagCategoryList,
  updateTagCount,
  updateTagCountVariables,
} from '~/graphqls/types'
import { SymbolTypes } from '~/pages/tag-generator'
import { clearStorage, getStorage, setStorage } from '~/utils'
import styles from './TagList.module.css'
import { useMutation } from '@apollo/client'
import { UPDATE_TAG_COUNT } from '~/graphqls/gqls'

export interface TagsTypes
  extends getGeneratorTags_aiArtworkResourceModule_spellTab_tags {
  weight: number
}

const REDUCE_SYMBOL_LEFT = '['
const REDUCE_SYMBOL_RIGHT = ']'
const PLUS_SYMBOL_BRACE_LEFT = '{'
const PLUS_SYMBOL_BRACE_RIGHT = '}'
const PLUS_SYMBOL_CURVE_LEFT = '('
const PLUS_SYMBOL_CURVE_RIGHT = ')'

interface TagGeneratorStorage {
  selectList: TagsTypes[]
  lang: string
}

export const TagList = ({
  selectList,
  setSelectList,
  selectSymbol,
  tagsData,
  currentTab,
  onModalOk,
}: {
  selectList: TagsTypes[]
  setSelectList: React.Dispatch<React.SetStateAction<TagsTypes[]>>
  selectSymbol: SymbolTypes
  tagsData?: getGeneratorTags_aiArtworkResourceModule_spellTab[]
  currentTab?: getTagsCategory_aiArtworkResourceModule_tagCategoryList
  onModalOk?: (copyText: string) => void // 在弹窗中共用
}) => {
  const inputRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const [copyText, setCopyText] = useState('')
  const [lang, setLang] = useState('zh')

  // 切换中英文时，暂时保存下当前的内容
  const [tempText, setTempText] = useState('')
  // 自定义编辑锁
  const editLockRef = useRef(false)
  // 分隔符
  const separator = lang === 'zh' ? '，' : ','

  const [updateTagCountAction] = useMutation<
    updateTagCount,
    updateTagCountVariables
  >(UPDATE_TAG_COUNT)

  const setText = () => {
    if (!!inputRef.current) {
      let text = ''
      let childNodes = inputRef.current.childNodes
      childNodes?.forEach(it => {
        // @ts-ignore
        text += it.innerText || it.nodeValue
      })
      setCopyText(text)
    } else {
      setCopyText('')
    }
  }

  useEffect(() => {
    setTempText(copyText)
    if (!!tempText.length) {
      setCopyText(tempText)
    } else {
      setText()
    }
  }, [lang])

  useEffect(() => {
    // 重新选择时清除temp数据
    setTempText('')
    if (editLockRef.current) {
      editLockRef.current = false
    } else {
      setText()
    }
  }, [selectList, selectSymbol])

  // 切换路由时，每次都从缓存里取
  useEffect(() => {
    if (tagsData != null) {
      const storage = getStorage('tagGenerator') as TagGeneratorStorage

      setLang(storage?.lang || 'zh')
      setSelectList(storage.selectList || [])
    }
  }, [router.asPath])

  const onEdit = (val: string) => {
    editLockRef.current = true

    setCopyText(val)

    const vals = val?.split(separator)

    const newSelectList = vals
      ?.filter(item => !!item)
      .map(item => {
        const isZH = lang === 'zh'

        const isExistInSelectList =
          selectList.findIndex(s => {
            return isZH
              ? s.name?.trim() === item?.trim()
              : s.englishName?.trim() === item?.trim()
          }) > -1

        if (isExistInSelectList) {
          return selectList.find(s =>
            isZH
              ? s.name?.trim() === item?.trim()
              : s.englishName?.trim() === item?.trim()
          ) as TagsTypes
        } else {
          // 对自定义输入的处理
          return {
            id: -1,
            name: isZH ? item?.trim() : null,
            englishName: !isZH ? item?.trim() : null,
            weight: 0,
          } as TagsTypes
        }
      })
    setSelectList(newSelectList || [])
  }

  const onCreate = () => {
    if (!copyText.length) {
      message.destroy()
      message.info('请选择标签')
      return
    }

    setStorage('aiParam', {
      prompt: copyText,
      model:
        currentTab?.model ||
        router.query.model ||
        AiCreateArtworkModel.STABLE_DIFFUSION,
      modelCode: currentTab?.modelCode,
    })

    if (tagsData != null) {
      /** 为了保留用户操作记录 */
      setStorage('tagGenerator', {
        selectList: selectList,
        lang: lang,
      } as TagGeneratorStorage)
    }

    updateTagCountAction({
      variables: {
        param: {
          tagEnNames: selectList.map(it => it.englishName),
        },
      },
    }).catch(() => {})

    router.push('/ai')
  }

  const onModalConfirm = () => {
    if (!copyText.length) {
      message.destroy()
      message.info('请选择标签')
      return
    }
    updateTagCountAction({
      variables: {
        param: {
          tagEnNames: selectList.map(it => it.englishName),
        },
      },
    }).catch(() => {})

    onModalOk?.(copyText)
  }

  const onLang = (val: string) => {
    if (val === lang) return
    setLang(val)
  }

  const onClear = () => {
    setSelectList([])
    clearStorage('tagGenerator')
  }

  const onRandomText = () => {
    const _list: TagsTypes[] = []
    tagsData?.forEach(list => {
      if (!!list.tags?.length) {
        _list.push({
          ...list?.tags[Math.floor(Math.random() * list.tags?.length)],
          weight: 0,
        })
      }
    })
    setSelectList(_list)
  }

  return (
    <div
      className="sticky h-full top-[80px]"
      style={{
        maxHeight: `calc(100vh - 80px)`,
      }}
    >
      <div
        className="bg-neutral-200 rounded-xl p-4 pb-12 h-full min-h-fit text-neutral-800 text-sm relative"
        style={{
          maxHeight: `calc(100% - 64px)`,
        }}
      >
        <div className={styles.textArea}>
          <Input.TextArea
            placeholder="请选择标签"
            value={copyText}
            onChange={e => {
              onEdit(e.target.value)
            }}
          />
        </div>
        <div className="hidden" ref={inputRef}>
          {selectList
            .filter(it => (lang === 'zh' ? !!it.name : !!it.englishName))
            .map((it, index) => (
              <Fragment key={index}>
                <span>
                  {it.weight < 0 &&
                    new Array(-it.weight).fill(REDUCE_SYMBOL_LEFT).join('')}
                  {it.weight > 0 &&
                    new Array(it.weight)
                      .fill(
                        selectSymbol === SymbolTypes.BRACE
                          ? PLUS_SYMBOL_BRACE_LEFT
                          : PLUS_SYMBOL_CURVE_LEFT
                      )
                      .join('')}
                  {lang === 'zh' ? it.name?.trim() : it.englishName?.trim()}
                  {it.weight < 0 &&
                    new Array(-it.weight).fill(REDUCE_SYMBOL_RIGHT).join('')}
                  {it.weight > 0 &&
                    new Array(it.weight)
                      .fill(
                        selectSymbol === SymbolTypes.BRACE
                          ? PLUS_SYMBOL_BRACE_RIGHT
                          : PLUS_SYMBOL_CURVE_RIGHT
                      )
                      .join('')}
                </span>
                {index <
                  selectList.filter(it =>
                    lang === 'zh' ? !!it.name : !!it.englishName
                  ).length -
                    1 && separator + (lang === 'zh' ? '' : ' ')}
              </Fragment>
            ))}
        </div>
        <div className="flex justify-between items-center absolute bottom-3 left-4 right-4 bg-neutral-200 text-neutral-500">
          <div className="flex justify-start items-center">
            <div className="p-[2px] rounded bg-neutral-300 flex items-center text-xs leading-4 text-center cursor-pointer mr-3">
              <div
                className={classNames(
                  'w-8 h-4 rounded-sm text-xs leading-[14px] font-medium',
                  {
                    'bg-primary text-neutral-800': lang === 'zh',
                  }
                )}
                onClick={() => {
                  onLang('zh')
                }}
              >
                中文
              </div>
              <div
                className={classNames(
                  'w-8 h-4 rounded-sm ml-1 text-xs leading-[14px] font-medium',
                  {
                    'bg-primary text-neutral-800': lang === 'en',
                  }
                )}
                onClick={() => {
                  onLang('en')
                }}
              >
                英文
              </div>
            </div>
            {tagsData?.length && (
              <div
                className="flex justify-center items-center mr-3 cursor-pointer transition-colors hover:text-primary text-xs"
                onClick={onRandomText}
              >
                <span>随机</span>
                <MyIcon icon="random" className="ml-1" />
              </div>
            )}
            <CopyText text={copyText as string}>
              <div className="flex justify-center items-center transition-colors hover:text-primary text-xs">
                <span>复制</span>
                <MyIcon icon="copy" className="ml-1" />
              </div>
            </CopyText>
          </div>
          <div
            className="cursor-pointer transition-colors hover:text-primary text-xs"
            onClick={onClear}
          >
            清空
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full">
        <MyBtn
          $dark
          type="primary"
          block
          size="large"
          className={styles.myBtn}
          onClick={onModalOk ? onModalConfirm : onCreate}
        >
          <div className="flex items-center justify-center">
            {onModalOk ? (
              '确定'
            ) : (
              <>
                <span>前往创作</span>
                <MyIcon icon="arrow-right-simple" className="text-[20px]" />
              </>
            )}
          </div>
        </MyBtn>
      </div>
    </div>
  )
}
